import React, { useCallback, useEffect, useMemo } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import TagsCRUDActions from '@mm/state/actions/TagsCRUDActions';

import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import TagModel from '@mm/state/model/TagModel';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { allTags } from '@mm/state/selectors/tags';

type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type OuterProps = {
  searchQuery: string;
  showAddForm: boolean;
  kind: 'otn' | 'regular' | 'service';
};

type Props = PropsFromState & PropsFromDispatch & OuterProps;

const SlideoutTags = ({
  searchQuery,
  showAddForm,
  tags,
  fbPageId,
  kind,
  getAllTags,
  deleteTag,
  createTag,
}: Props) => {
  const tagsOfKind = tags.filter(tag => tag.kind === kind);
  const onDeleteTag = useCallback(
    (tag: TagModel) => deleteTag(tag.name as string, fbPageId, tag.kind),
    [],
  );
  const onAddTag = (name: string) => {
    createTag(name, fbPageId, kind);
  };

  useEffect(() => {
    if (fbPageId) {
      getAllTags(fbPageId, kind);
    }
  }, [fbPageId]);

  const filteredTags = useMemo(() => {
    const query = searchQuery && searchQuery.toLowerCase();

    return query
      ? tagsOfKind.filter(
          tag => tag.name && tag.name.toLowerCase().includes(query),
        )
      : tagsOfKind;
  }, [tagsOfKind, searchQuery]);

  return (
    <>
      {showAddForm && <Menu.AddForm placeholder="New tag" onAdd={onAddTag} />}
      <Menu.Content>
        {filteredTags.map(tag => (
          <Menu.Item key={tag.name} item={tag} onDelete={onDeleteTag} />
        ))}
      </Menu.Content>
    </>
  );
};

const mapStateToProps = (state: StoreState) => ({
  tags: allTags(state),
  fbPageId: activeFacebookPageIdSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getAllTags: (pageId: number, kind: 'otn' | 'regular' | 'service') =>
    dispatch(TagsCRUDActions.getAll(pageId, false, [kind])),
  deleteTag: (
    name: string,
    fbPageId: number,
    kind?: 'otn' | 'regular' | 'service',
  ) => dispatch(TagsCRUDActions.delete(name, fbPageId, kind)),
  createTag: (
    name: string,
    fbPageId: number,
    kind?: 'otn' | 'regular' | 'service',
  ) => dispatch(TagsCRUDActions.create(name, fbPageId, kind)),
});

const SlideoutTagsWithState = connect<
  PropsFromState,
  PropsFromDispatch,
  OuterProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(SlideoutTags);

export { SlideoutTagsWithState as SlideoutTags };
